import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import clsx from 'clsx';
import { PointerEvent, useCallback, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ClientMatchedResponse, ClientTextReviewResponse, ProductWidget } from '@vestico/model';

import { Button, Footer, GlobalsContext, UserContext } from '@vestico/shared';

import { userDataCompletedForAnyProduct } from '../model/helpers';
import { MatchedReviewContent } from './ReviewContentMatched';
import { TextReviewContent } from './ReviewContentText';
import style from './ReviewDetails.module.css';
import { ReviewDetailsHeader } from './ReviewDetailsHeader';

export interface ReviewDetailsProps {
  response: ClientMatchedResponse | ClientTextReviewResponse;
  products: ProductWidget[] | null | undefined;
  showProducts?: boolean;
  onProductLinkClick?: (product: ProductWidget) => void;
  showSizeRecToggle?: boolean;
  onSizeRecToggle?: () => void;
  headerMarginRight?: number;
  showBeginUserMatchInfoSection?: boolean;
  showResetUserButton?: boolean;
  anyResponseHasMatch?: boolean;
  onBeginUserMatchInfo?: () => void;
  showFooter?: boolean;
  scrollEnabled: boolean;
  onIsScrollable?: (isScrollable: boolean) => void;
  onScrollToTop?: (scrolledToTop: boolean) => void;
  onContentIsEmpty?: (empty: boolean) => void;
  showLikeInteraction?: boolean;
}

export const ReviewDetails = ({
  response,
  products,
  showProducts,
  onProductLinkClick,
  showSizeRecToggle,
  onSizeRecToggle,
  headerMarginRight,
  showBeginUserMatchInfoSection,
  showResetUserButton,
  anyResponseHasMatch,
  onBeginUserMatchInfo,
  showFooter,
  scrollEnabled,
  onIsScrollable,
  onScrollToTop,
  onContentIsEmpty,
  showLikeInteraction,
}: ReviewDetailsProps) => {
  const { type } = response;
  const { t } = useTranslation();

  const { user, reloadUser } = useContext(UserContext);
  const { stylesConfig } = useContext(GlobalsContext);
  const { customPrivacyURL } = stylesConfig.stylesConfig;

  const isTextReview = type === 'textreview';

  const contentRef = useRef<HTMLDivElement | null>(null);
  const nodeRef = useRef<HTMLDivElement>(null);

  const reviewContentPart = isTextReview ? (
    <TextReviewContent
      response={response}
      products={products}
      onBeginUserMatchInfo={onBeginUserMatchInfo}
      showBeginUserMatchInfoSection={showBeginUserMatchInfoSection}
      anyResponseHasMatch={anyResponseHasMatch}
      showProducts={showProducts}
      onProductLinkClick={onProductLinkClick}
      ref={contentRef}
    />
  ) : (
    <MatchedReviewContent
      response={response}
      products={products}
      showProducts={!!showProducts}
      onProductLinkClick={onProductLinkClick}
      ref={contentRef}
    />
  );

  useEffect(() => {
    const { current: contentElement } = contentRef;

    if (!contentElement || !onContentIsEmpty) {
      return;
    }
    const { clientHeight } = contentElement;

    onContentIsEmpty(clientHeight === 0);
  }, [onContentIsEmpty, response, showProducts]);

  const reset = useCallback(() => {
    if (user) {
      if (reloadUser) {
        reloadUser(true);
      }
    }
  }, [reloadUser, user]);

  const rightFooterActions =
    user && products && userDataCompletedForAnyProduct(products, user) && showResetUserButton
      ? [
          <div key="reset">
            <Button content={t('LOC_RESET_BODY_INFO_BUTTON')} onClick={reset} plain size="small" />
          </div>,
        ]
      : undefined;

  const isScrolledToTop = useCallback(() => {
    if (!nodeRef) {
      return true;
    }
    const { current } = nodeRef;
    if (!current) {
      return true;
    }

    const { scrollTop } = current;
    const scrolledToTop = Math.floor(scrollTop) <= 0;
    return scrolledToTop;
  }, []);

  const isScrollable = useCallback(() => {
    if (!nodeRef) {
      return false;
    }
    const { current } = nodeRef;
    if (!current) {
      return false;
    }

    const { scrollHeight, clientHeight } = current;
    const scrollable = scrollHeight > clientHeight;
    return scrollable;
  }, []);

  useEffect(() => {
    const { current } = nodeRef;
    if (!current) {
      return;
    }

    if (scrollEnabled) {
      disableBodyScroll(current);
    } else {
      enableBodyScroll(current);
    }

    if (onIsScrollable) {
      onIsScrollable(isScrollable());
    }
  }, [isScrollable, nodeRef, onIsScrollable, scrollEnabled]);

  // Enable back body scroll on unmount
  useEffect(() => {
    return () => {
      const { current } = nodeRef;
      if (!current) {
        return;
      }
      enableBodyScroll(current);
    };
  }, []);

  const handleScroll = useCallback(() => {
    const { current } = nodeRef;
    if (!current) {
      return;
    }

    if (onScrollToTop) {
      onScrollToTop(isScrolledToTop());
    }
  }, [nodeRef, isScrolledToTop, onScrollToTop]);

  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      const enableScrolling = scrollEnabled && !isScrolledToTop && isScrollable;
      if (enableScrolling) {
        e.stopPropagation();
        e.preventDefault();
      }
    },
    [scrollEnabled, isScrolledToTop, isScrollable]
  );

  return (
    <div className={clsx(style.root)}>
      <ReviewDetailsHeader
        response={response}
        headerMarginRight={headerMarginRight}
        showLikeInteraction={showLikeInteraction}
        showSizeRecToggle={showSizeRecToggle}
        onSizeRecToggle={onSizeRecToggle}
      />
      <div className={clsx(style.content)} ref={nodeRef} onPointerDown={handlePointerDown} onScroll={handleScroll}>
        {reviewContentPart}
      </div>
      {showFooter ? (
        <div className={clsx(style.footer)}>
          <Footer showBanner showPrivacyLink rightActions={rightFooterActions} bordered fullWidth customPrivacyURL={customPrivacyURL} />
        </div>
      ) : null}
    </div>
  );
};
